import SubHeaders from '@/components/Headers/SubHeaders'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import React, { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { VCardCreatePayload } from '../../types/payload'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import AddIcons from '@/icons/AddIcons'
import { FormItem } from 'vnetwork-ui'
import toast from 'react-hot-toast'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { createVCard, getPresignedURL, UpdateVCard } from '../../services'
import axios from 'axios'
import useAuthStore from '@/stores/useAuthStore'
import { yupResolver } from '@hookform/resolvers/yup'
import { vCardSchema } from './yup'
import { VCARD_SOCIAL_PLATFORMS, VCARD_EXTRA_POSITIONS } from '../../constants'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { VCard } from '../../types/response'
import { useNavigate } from 'react-router-dom'
import ImageForm from '@/components/Form/Shared/ImageInput'
import { useTranslation } from 'react-i18next'
import { GridInformation } from 'vnetwork-ui'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
interface VCardCreateProps extends IPermissionProps {
    pageType?: 'create' | 'update'
    vCard?: VCard
    refetchData: () => void
}
const VCardCreate: React.FC<VCardCreateProps> = ({
    pageType = 'create',
    vCard,
    refetchData,
}) => {
    const { t: translate } = useTranslation()
    const navigate = useNavigate()
    const { employee } = useAuthStore()
    const { refetchCurrent } = useDataGridRefetch({ reactQueryKey: 'vcards' })
    const { refetchAll } = useDataGridRefetch({
        reactQueryKey: 'vcard-current-employee',
    })
    const { mutateAsync: callCreateVCard, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: createVCard,
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.VCARDS,
                    QUERY_KEYS.VCARD_CURRENT_EMPLOYEE,
                ],
                successMessage: 'VCard created successfully',
                onSuccess: () => {
                    refetchCurrent()
                    refetchAll()
                    navigate('/account/vcards')
                },
            }
        )
    const { mutateAsync: callUpdateVCard, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: UpdateVCard,
            },
            {
                reactQueryKeys: QUERY_KEYS.VCARDS,
                successMessage: 'VCard updated successfully',
                onSuccess: () => {
                    if (refetchData) refetchData()
                    refetchCurrent()
                    refetchAll()
                    navigate('/account/vcards')
                },
            }
        )
    const [updatedAvatar, setUpdatedAvatar] = useState<boolean>(false)
    const { control, setError, setValue, watch, handleSubmit } =
        useForm<VCardCreatePayload>({
            defaultValues: {
                avatar: pageType === 'create' ? '' : vCard?.avatar,
                name: pageType === 'create' ? '' : vCard?.name,
                phone: pageType === 'create' ? '' : vCard?.phone,
                address: pageType === 'create' ? '' : vCard?.address,
                description: pageType === 'create' ? '' : vCard?.description,
                email: pageType === 'create' ? '' : vCard?.email,
                position:
                    pageType === 'create'
                        ? employee?.position_name
                        : vCard?.position,
                socials:
                    pageType === 'create'
                        ? [
                              {
                                  key: 'facebook',
                                  value: 'https://facebook.com',
                              },
                          ]
                        : vCard?.socials,
                socials_delete: [],
            },
            //@ts-ignore
            resolver: yupResolver(vCardSchema),
        })
    const [file, setValueFile] = useState<File | null>(null)

    const { append, remove, fields } = useFieldArray({
        control,
        name: 'socials',
    })

    const onSubmit: any = async (data: VCardCreatePayload) => {
        if (!file && updatedAvatar)
            return toast.error('Không tìm thấy ảnh avatar')
        const socialsMap = new Map()
        for (const social of data?.socials || []) {
            if (socialsMap.has(social.key)) {
                toast.error('Duplicate social platform')
                return
            }
            socialsMap.set(social.key, true)
        }
        const type_file =
            file?.name?.split('.')?.[(file?.name?.split('.')?.length || 0) - 1]
        const key = `vcard_avatar_${Date.now()}.${type_file}`
        try {
            if (updatedAvatar) {
                const response = await getPresignedURL({ key })
                const putResponse = await axios.put(
                    response?.result?.url,
                    file,
                    {
                        headers: { 'Content-Type': file?.type },
                    }
                )
                if (putResponse.status !== 200) return
                data.avatar = import.meta.env.APP_STORAGE + '/avatars/' + key
            }
            if (pageType === 'create') {
                await callCreateVCard(data)
            } else {
                await callUpdateVCard({
                    id: vCard?.id || 0,
                    vCard: data,
                })
            }
        } catch (errors) {
            toast.error('' + errors)
        }
    }

    const handleInputAvatar = () => {
        const input = document.createElement('input')

        input.type = 'file'

        input.click()

        input.onchange = (e) => {
            const file = (e?.target as HTMLInputElement)?.files?.[0]
            if (!file?.type?.startsWith('image')) {
                return setError('avatar', { message: 'Only file image!' })
            }

            if (file.size / 1000000 > 10) {
                return setError('avatar', {
                    message: 'Only accept files under 10MB!',
                })
            }

            if (watch('avatar')) {
                setValueFile(null)
                URL.revokeObjectURL(watch('avatar'))
            }
            setValue('avatar', URL.createObjectURL(file))
            setValueFile(file)
            setError('avatar', { message: undefined })
            input.remove()
        }
        setUpdatedAvatar(true)
    }

    const handleAddSocial = () => {
        if (fields.length >= 7)
            return toast.error('Cant create more than 7 socials')
        const social = {
            key: '',
            value: '',
        }
        append(social)
    }

    const handleRemoveSocial = (index: number) => {
        if (pageType === 'update') {
            setValue('socials_delete', [
                ...watch('socials_delete'),
                fields[index],
            ])
        }
        remove(index)
    }

    const gridInformation = [
        {
            label: translate('full-name'),
            value: (
                <FormItem
                    control={control}
                    name="name"
                    type="textfield"
                    size="small"
                    className="text-gray-500"
                    placeholder={translate('full-name')}
                />
            ),
        },
        {
            label: translate('position'),
            value: (
                <FormItem
                    control={control}
                    name="position"
                    type="select"
                    size="small"
                    placeholder={translate('position')}
                    options={(() => {
                        const defaultPos = employee?.position_name || ''
                        const savedPos =
                            pageType === 'update' ? vCard?.position || '' : ''
                        const all = [
                            defaultPos,
                            savedPos,
                            ...VCARD_EXTRA_POSITIONS,
                        ].filter(Boolean)
                        const unique = [...new Set(all)]
                        return unique.map((p) => ({ label: p, value: p }))
                    })()}
                />
            ),
        },
        {
            label: 'Email',
            value: (
                <FormItem
                    control={control}
                    name="email"
                    type="textfield"
                    size="small"
                    className="text-gray-500"
                    placeholder="Email"
                />
            ),
        },
        {
            label: translate('phone'),
            value: (
                <FormItem
                    control={control}
                    name="phone"
                    type="textfield"
                    size="small"
                    className="text-gray-500"
                    placeholder={translate('phone')}
                />
            ),
        },
        {
            label: translate('description'),
            value: (
                <FormItem
                    control={control}
                    name="description"
                    type="textarea"
                    size="small"
                    placeholder={translate('description')}
                    className="text-gray-500"
                />
            ),
        },
        {
            label: translate('address'),
            value: (
                <FormItem
                    control={control}
                    name="address"
                    type="textfield"
                    size="small"
                    className="text-gray-500"
                    placeholder={translate('address')}
                />
            ),
        },
    ]

    return (
        <div className="w-full">
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.vcardList,
                        title: translate('vcard_list'),
                    },
                    {
                        path:
                            pageType === 'create'
                                ? paths.vcardCreatePage
                                : paths.vcardEditPage.replace(':id', ''),
                        title: translate(
                            pageType === 'create'
                                ? 'create-vcard'
                                : `update-vcard`
                        ),
                    },
                ]}
            />
            <form
                className="mx-auto"
                onSubmit={handleSubmit(onSubmit)}
                data-tour="vcard-form"
            >
                <div className="flex flex-col py-4 ">
                    <div
                        className="w-full bg-white p-4 rounded-lg"
                        data-tour="vcard-avatar"
                    >
                        <ImageForm
                            control={control}
                            name="avatar"
                            handleInput={handleInputAvatar}
                            type="avatar"
                        />
                    </div>
                    <div data-tour="vcard-info">
                        <GridInformation
                            data={gridInformation}
                            className=""
                            head={{
                                title: translate('vcard_information'),
                            }}
                        />
                    </div>
                    <div
                        className="grid grid-cols-4 gap-2 mt-2 p-4 bg-white rounded-lg"
                        data-tour="vcard-socials"
                    >
                        <div className="col-span-3 flex">
                            <Typogaphy
                                size="small"
                                className="text-gray-500 flex items-center justify-center h-full"
                            >
                                {translate('socials')}
                            </Typogaphy>
                        </div>
                        <div className="col-span-1">
                            <Button
                                onClick={() => handleAddSocial()}
                                type="button"
                                className="float-end"
                                startIcon={<AddIcons />}
                            >
                                {translate('add_social')}
                            </Button>
                        </div>
                        <div className="col-span-4">
                            <div className="grid grid-cols-1 gap-2">
                                {fields.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className="grid grid-cols-12 gap-2"
                                    >
                                        <div className="col-span-6">
                                            <FormItem
                                                control={control}
                                                type="select"
                                                name={`socials.${index}.key`}
                                                size="small"
                                                options={VCARD_SOCIAL_PLATFORMS}
                                                placeholder={translate(
                                                    'select_platform'
                                                )}
                                            />
                                        </div>
                                        <div className="col-span-5">
                                            <FormItem
                                                control={control}
                                                name={`socials.${index}.value`}
                                                type="textfield"
                                                size="small"
                                                className="text-gray-500"
                                                placeholder={translate(
                                                    'social_link'
                                                )}
                                            />
                                        </div>
                                        <div>
                                            <IconButton className="float-end">
                                                <DeleteOutlineIcons
                                                    onClick={() =>
                                                        handleRemoveSocial(
                                                            index
                                                        )
                                                    }
                                                />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div data-tour="vcard-submit">
                            <Button
                                className="col-span-4"
                                type="submit"
                                disabled={isCreating || isUpdating}
                            >
                                {translate(
                                    pageType === 'create' ? 'create' : 'update'
                                )}{' '}
                                VCard
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}

export const PageVCardCreate = (props: VCardCreateProps) => {
    return <VCardCreate {...props} />
}
